import { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { Environment, GraphQLTaggedNode } from 'react-relay';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { IntlProvider } from 'dibs-react-intl';
import serverVars from 'server-vars';

import { addLoginHandlers, initResetUpdateUserState } from '../utils/loginHandlers';
import { actionCreators } from '../actions';

type HeaderClientArgs = {
    relayEnv: Environment;
    node: Element;
    store: Store;
};

type HeaderClientResponse = {
    store: Store;
    getBuyerHeaderUiState: () => Store;
};

type MessageBannerRendererProps = {
    initialVariables: {
        hasUserId: boolean;
        fetchRegionalInfo: boolean;
        fetchTradeNav: boolean;
        fetchNav: boolean;
        userId: string;
        userIds: string[];
        isClient: boolean;
        zipCode: string;
        checkoutUserId: string;
        hasCheckoutUserId: boolean;
    };
    environment: Environment;
    query: GraphQLTaggedNode;
    messageBannerRenderer?: ({
        hasRootPendingRefetch,
        hasUserId,
        isClient,
        isUserTypeTrade,
        useGlobalFallbackMessage,
        userId,
        relayEnvironment,
        onBannerClose,
        hasTradeUserId,
    }: {
        hasRootPendingRefetch: boolean;
        hasUserId: boolean;
        isClient: boolean;
        isUserTypeTrade: boolean;
        useGlobalFallbackMessage: boolean;
        userId: string;
        relayEnvironment: Environment;
        onBannerClose: () => void;
        hasTradeUserId: boolean;
    }) => ReactNode;
};

const getBuyerHeaderClient = async ({
    node,
    store,
    relayEnv,
}: HeaderClientArgs): Promise<HeaderClientResponse> => {
    const isDesktop = serverVars.get('settings.isDesktop');
    const isTablet = serverVars.get('settings.isTablet');
    const isMobile = serverVars.get('settings.isMobile');
    const isUpdatedMessageBanner = serverVars.get('enablers.updatedMessageBanner');

    let navPromise;
    let messageBannerPromise: $TSFixMe = Promise.resolve({});

    if (isDesktop) {
        navPromise = import(
            /* webpackChunkName: "responsiveHeader" */ '../components/responsiveHeader'
        );
    } else {
        navPromise = import(/* webpackChunkName: "mobileHeader" */ '../components/mobileHeader');
    }

    if (isDesktop || isTablet || (isMobile && isUpdatedMessageBanner)) {
        messageBannerPromise = import(
            /* webpackChunkName: "MessageBanner" */ '../components/MessageBanner/MessageBanner'
        );
    }

    const { HeaderRenderer, headerRootQuery, headerInitialVariables } = await navPromise;
    const { default: MessageBannerRenderer } = await messageBannerPromise;

    const locale = serverVars.get('locale');
    const messages = serverVars.get('messages');

    let props: MessageBannerRendererProps = {
        initialVariables: headerInitialVariables,
        environment: relayEnv,
        query: headerRootQuery,
    };

    if (MessageBannerRenderer) {
        props = {
            ...props,
            messageBannerRenderer: messageBannerRendererProps => (
                <MessageBannerRenderer {...messageBannerRendererProps} useGlobalFallbackMessage />
            ),
        };
    }

    addLoginHandlers(() => {
        store.dispatch(actionCreators.setUpdateUserState(true));
    });
    initResetUpdateUserState(() => store.dispatch(actionCreators.setUpdateUserState(false)));

    ReactDOM.hydrate(
        <IntlProvider locale={locale} messages={messages}>
            <Provider store={store}>
                <HeaderRenderer {...props} />
            </Provider>
        </IntlProvider>,
        node
    );

    return {
        store,
        getBuyerHeaderUiState: store.getState,
    };
};

export { getBuyerHeaderClient };
